import * as Sentry from '@sentry/nextjs';

/**
 * Error utility functions for consistent error handling across the application
 */

/**
 * Determines if an error provides actionable context for users
 * These are errors where users might be able to work around the issue
 * (e.g., "missing fitting" - user might change configuration)
 * @param error - Error object or error message string
 * @returns true if error is user-fixable or provides actionable context
 */
export function isUserFixableError(error: unknown): boolean {
  const errorMessage = error instanceof Error
    ? error.message.toLowerCase()
    : String(error).toLowerCase();

  // Start with known user-actionable errors
  // Add more as we discover them
  return (
    errorMessage.includes('could not find fitting')
    || errorMessage.includes('could not find any entity of type "fittingvariant"')
    || errorMessage.includes('missing fitting')
  );
}

/**
 * Determines if an error is a system/infrastructure error
 * These are errors that indicate backend/system issues requiring support intervention
 * (e.g., client closed, connection pool issues, invalid URLs)
 * @param error - Error object or error message string
 * @returns true if error is a system error
 */
export function isSystemError(error: unknown): boolean {
  const errorMessage = error instanceof Error
    ? error.message.toLowerCase()
    : String(error).toLowerCase();

  return (
    errorMessage.includes('client is closed')
    || errorMessage.includes('client closed')
    || errorMessage.includes('connection pool')
    || errorMessage.includes('redis')
    || errorMessage.includes('database connection')
    || errorMessage.includes('invalid url')
    || errorMessage.includes('failed to request')
    || errorMessage.includes('network error')
    || errorMessage.includes('econnreset')
    || errorMessage.includes('econnrefused')
  );
}

/**
 * Generates a unique support code (10 characters) for Sentry event tagging.
 * Uses timestamp + random to ensure uniqueness.
 * @returns 10-character support code string
 */
function generateSupportCode(): string {
  // Use timestamp (base36) + random chars to create a unique 10-char code
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  const combined = (timestamp + random).substring(0, 10);
  // Pad if needed (shouldn't happen, but safety check)
  return combined.padEnd(10, '0').substring(0, 10);
}

/**
 * Captures a Sentry error/exception and sets a truncated support code tag for easier lookup.
 * Accepts either an Error object or a message string (wrapped into an Error).
 * The support code is generated before capture and set on the scope so it's included in the event.
 * @param error - Error object or message to capture in Sentry
 * @param options - Optional Sentry capture context (level, extra, tags, etc.)
 * @returns Object with both the full Sentry event ID and the support code
 */
export function captureSupportEvent(
  error: Error | string,
  // Use loose typing here to avoid coupling to Sentry's internal types
  // while still allowing capture context to be passed through.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any,
): { eventId: string; supportCode: string } {
  const errorObj = error instanceof Error ? error : new Error(error);

  // Generate support code BEFORE capturing so we can set it on the scope
  const supportCode = generateSupportCode();

  // Use withScope to ensure the tag is included with the captured event
  let eventId: string;
  Sentry.withScope((scope) => {
    // Set the support_code tag on this scope
    scope.setTag('support_code', supportCode);

    // Apply any additional tags from options if provided
    if (options?.tags) {
      Object.entries(options.tags).forEach(([key, value]) => {
        scope.setTag(key, value as string);
      });
    }

    // Apply level from options if provided
    if (options?.level) {
      scope.setLevel(options.level);
    }

    // Capture the exception within this scope (tag will be included)
    eventId = Sentry.captureException(errorObj, {
      ...options,
      // Remove tags/level from options since we've set them on the scope
      tags: undefined,
      level: undefined,
    });
  });

  return { eventId, supportCode };
}

/**
 * Generates a user-facing error message based on error classification
 * @param errorMessage - The error message to display
 * @param supportCode - Truncated support code for Sentry event (e.g., first 10 chars)
 * @param options - Additional options for message formatting
 * @returns Formatted error message for display to users
 */
export function getUserFacingErrorMessage(
  errorMessage: string,
  supportCode?: string,
  options?: {
    productName?: string;
    wasRetryable?: boolean;
    retriesExhausted?: boolean;
    simplifyError?: (msg: string) => string;
    context?: string; // e.g., "BOM generation failed" or generic context
  },
): string {
  const {
    productName,
    wasRetryable = false,
    retriesExhausted = false,
    simplifyError,
    context = 'Error occurred',
  } = options || {};

  // Classify error type
  const isUserFixable = isUserFixableError(errorMessage);
  const isSystemErr = isSystemError(errorMessage) || (wasRetryable && retriesExhausted);

  // Format support code if available (already truncated by caller)
  const formattedSupportCode = supportCode?.trim();
  const supportText = formattedSupportCode ? ` Support code: ${formattedSupportCode}` : '';

  // Build message based on error type
  if (isUserFixable && !(wasRetryable && retriesExhausted)) {
    // User-fixable errors: show specific message with support code
    const simplifiedError = simplifyError ? simplifyError(errorMessage) : errorMessage;
    if (productName) {
      return `${context} for ${productName}: ${simplifiedError}.${supportText}`;
    }
    return `${context}: ${simplifiedError}.${supportText}`;
  }

  if (isSystemErr) {
    // System errors (including exhausted retries): show generic message with support code
    return `Something went wrong. Please contact us.${supportText}`;
  }

  // Fallback for unknown error types: show generic message with support code
  if (productName) {
    return `${context} for ${productName}.${supportText}`;
  }
  return `${context}.${supportText}`;
}
